import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    addFCTransferLineItem,
    cancelFCTransfer,
    createFCTransfer,
    downloadFCTransferBol,
    fetchFCTransferCreatedByUsers,
    fetchFCTransferStatuses,
    getFCTransferById,
    mapTransferIdToApiPathParam,
    receiveFCTransferItemAtZero,
    removeFCTransferLineItem,
    saveFCTransferReceiptLine,
    searchAvailableTransferLots,
    searchFCTransfers,
} from 'api/warehouse/fcTransfers';

/**
 * @param {unknown} facilityId
 * @returns {number | null}
 */
function normalizePositiveFacilityId(facilityId) {
    if (facilityId === undefined || facilityId === null || facilityId === '') {
        return null;
    }
    const n = Number(facilityId);
    if (!Number.isFinite(n) || n <= 0) {
        return null;
    }
    return n;
}

export const fcTransferQueryKeys = {
    all: ['fcTransfers'],
    searchPaginated: (params) => ['fcTransfers', 'v1', 'search', 'paginated', params],
    /** @param {string | number | undefined | null} transferId */
    detail: (transferId) => ['fcTransfers', 'v1', 'detail', transferId],
    /** Eligible transfer lots for a source facility (create / edit lot picker). */
    availableLots: (facilityId) => ['fcTransfers', 'v1', 'transferInventory', facilityId],
    /** Filter dropdown: allowed transfer statuses from metadata endpoint. */
    transferStatuses: () => ['fcTransfers', 'v1', 'transferStatuses'],
    /** Filter dropdown: users with CREATE_FC_TRANSFER (admin role-users endpoint). */
    createdByUsers: () => ['fcTransfers', 'v1', 'createdByUsers'],
};

/**
 * Single-page (cursor) FC Transfers list search; mirrors `usePackagingPurchaseOrdersV2Search`.
 *
 * @param {Object} [params={}] - Merged list filters + pagination + sort
 * @param {Object} [options={}] - React Query options (`enabled`, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<{rows: Array, pagination: {hasMore?: boolean, nextCursor?: *, totalCount?: number}}, Error>}
 */
export function useFCTransfersSearch(params = {}, options = {}) {
    return useQuery({
        queryKey: fcTransferQueryKeys.searchPaginated(params),
        queryFn: async ({ signal }) => {
            const response = await searchFCTransfers(params, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch FC transfers');
            }
            return response.data;
        },
        staleTime: 30 * 1000,
        ...options,
    });
}

/**
 * Status strings for the FC Transfers filter dropdown (`{ value, label }` per select control).
 *
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Array<{ value: string, label: string }>, Error, Array<{ value: string, label: string }>, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{ value: string, label: string }>, Error>}
 */
export function useFCTransferStatusOptions(queryOptions = {}) {
    return useQuery({
        queryKey: fcTransferQueryKeys.transferStatuses(),
        queryFn: async ({ signal }) => {
            const statuses = await fetchFCTransferStatuses({ signal });
            return statuses.map((status) => ({
                value: String(status),
                label: String(status),
            }));
        },
        staleTime: 5 * 60 * 1000,
        ...queryOptions,
    });
}

/**
 * Created By filter options: `{ value: auth user id, label: email }`.
 *
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Array<{ value: number, label: string }>, Error, Array<{ value: number, label: string }>, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{ value: number, label: string }>, Error>}
 */
export function useFCTransferCreatedByUserOptions(queryOptions = {}) {
    return useQuery({
        queryKey: fcTransferQueryKeys.createdByUsers(),
        queryFn: async ({ signal }) => {
            const users = await fetchFCTransferCreatedByUsers({ signal });
            return users.map((u) => {
                const id = u?.id;
                const email = typeof u?.email === 'string' ? u.email.trim() : '';
                const label = email || `User ${id}`;
                return { value: id, label };
            });
        },
        staleTime: 5 * 60 * 1000,
        ...queryOptions,
    });
}

/**
 * FC transfer entity (header + items); response shape matches read API `data.transfer`.
 *
 * @param {Object} [args={}]
 * @param {boolean} [args.enabled=true] - Passed through to `useQuery` (default true; combine with valid id in callers)
 * @param {string | number | undefined | null} [args.transferId] - Transfer id from route or selection
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Object, Error, Object, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}] - Additional React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<Object, Error>}
 */
export function useFCTransferById({ enabled = true, transferId, ...queryOptions } = {}) {
    const normalizedId = mapTransferIdToApiPathParam(transferId);
    return useQuery({
        queryKey: fcTransferQueryKeys.detail(normalizedId ?? transferId ?? null),
        queryFn: async ({ signal }) => {
            const response = await getFCTransferById(transferId, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch FC transfer');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            const transferPk = transfer?.id ?? transfer?.transferId;
            if (transferPk === undefined || transferPk === null || transferPk === '') {
                throw new Error('FC transfer response missing transfer id');
            }
            return transfer;
        },
        enabled: Boolean(normalizedId) && enabled,
        staleTime: 30 * 1000,
        ...queryOptions,
    });
}

/**
 * Eligible lots/locations for FC transfer line-item assembly at `facilityId`.
 *
 * @param {Object} [args={}]
 * @param {boolean} [args.enabled=true]
 * @param {number | string | undefined | null} [args.facilityId] - Source facility id
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Array, Error, Array, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array, Error>}
 */
export function useAvailableTransferLots({ enabled = true, facilityId, ...queryOptions } = {}) {
    const id = normalizePositiveFacilityId(facilityId);

    return useQuery({
        queryKey: fcTransferQueryKeys.availableLots(id ?? facilityId ?? null),
        queryFn: async ({ signal }) => {
            const response = await searchAvailableTransferLots({ facilityId: id }, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch transfer inventory');
            }
            const rows = response?.data?.transferInventory;
            if (!Array.isArray(rows)) {
                throw new Error('Transfer inventory response missing transferInventory array');
            }
            return rows;
        },
        enabled: Boolean(id) && enabled,
        staleTime: 30 * 1000,
        ...queryOptions,
    });
}

/**
 * Creates a pending FC transfer; on success invalidates all `fcTransfers` queries so the list refetches.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, Object>, 'mutationFn'>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, Object>}
 */
export function useCreateFCTransfer(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async (payload) => {
            const response = await createFCTransfer(payload);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to create FC transfer');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Adds a line item to a pending FC transfer; on success invalidates FC transfer queries.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, { item: Object, transferId: string | number }>, 'mutationFn'>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, { item: Object, transferId: string | number }>}
 */
export function useAddFCTransferLineItem(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ item, transferId }) => {
            const response = await addFCTransferLineItem(transferId, item);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to add FC transfer line item');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Removes a line item from a pending FC transfer; on success invalidates FC transfer queries.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, { transferId: string | number, transferItemId: string | number }>, 'mutationFn'>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, { transferId: string | number, transferItemId: string | number }>}
 */
export function useRemoveFCTransferLineItem(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ transferId, transferItemId }) => {
            const response = await removeFCTransferLineItem(transferId, transferItemId);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to remove FC transfer line item');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Cancels a pending FC transfer; on success invalidates FC transfer queries.
 *
 * @param {import('@tanstack/react-query').UseMutationOptions<Object, Error, string | number>} [mutationOptions={}]
 */
export function useCancelFCTransfer(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async (transferId) => {
            const response = await cancelFCTransfer(transferId);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to cancel FC transfer');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Downloads a transfer BOL PDF for a transfer id.
 *
 * @param {import('@tanstack/react-query').UseMutationOptions<
 *   Object,
 *   Error,
 *   { transferId: string | number, bolFormData: Object }
 * >} [mutationOptions={}]
 */
export function useDownloadFCTransferBol(mutationOptions = {}) {
    return useMutation({
        mutationFn: async ({ bolFormData, transferId }) => {
            const response = await downloadFCTransferBol(transferId, bolFormData);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to download FC transfer BOL');
            }
            return response?.data ?? {};
        },
        ...mutationOptions,
    });
}

/**
 * Saves one FC transfer receipt line draft; on success invalidates the transfer detail query.
 * Returns `response.data` from the API envelope unchanged (`lotId`, `pallets`, `receivedNumCases`, …).
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<
 *   Object,
 *   Error,
 *   { transferId: string | number, transferItemId: string | number, payload: Object }
 * >, 'mutationFn'>} [mutationOptions={}]
 */
export function useSaveFCTransferReceiptLine(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ payload, transferId, transferItemId }) => {
            const response = await saveFCTransferReceiptLine({ transferId, transferItemId, payload });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to save FC transfer receipt line');
            }
            return response?.data ?? {};
        },
        onSuccess: (...args) => {
            const [, variables] = args;
            const { transferId } = variables ?? {};
            const detailKeyId = mapTransferIdToApiPathParam(transferId) ?? transferId ?? null;
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.detail(detailKeyId) });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Receives an FC transfer line at zero quantity; on success invalidates the transfer detail query.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<
 *   Object,
 *   Error,
 *   { transferId: string | number, transferItemId: string | number }
 * >, 'mutationFn'>} [mutationOptions={}]
 */
export function useReceiveFCTransferItemAtZero(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ transferId, transferItemId }) => {
            const response = await receiveFCTransferItemAtZero({ transferId, transferItemId });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to receive FC transfer item at zero');
            }
            return response?.data ?? {};
        },
        onSuccess: (...args) => {
            const [, variables] = args;
            const { transferId } = variables ?? {};
            const detailKeyId = mapTransferIdToApiPathParam(transferId) ?? transferId ?? null;
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.detail(detailKeyId) });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}
